import { QueryClient } from '@tanstack/react-query';

// Create a query client with caching disabled for immediate fresh data
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Disable all caching
      staleTime: 0, // Always consider data stale
      gcTime: 0, // Don't keep data in cache
      // Retry failed requests up to 3 times
      retry: 3,
      // Retry with exponential backoff
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      // Always refetch on window focus
      refetchOnWindowFocus: true,
      // Always refetch on reconnect
      refetchOnReconnect: 'always',
      // Disable background refetch
      refetchInterval: false,
      // Network mode - always network first
      networkMode: 'online',
    },
    mutations: {
      // Retry mutations once
      retry: 1,
      // Network mode for mutations
      networkMode: 'online',
    },
  },
});

// Query keys for consistent caching
export const queryKeys = {
  // Auth related
  auth: {
    token: ['auth', 'token'],
    user: ['auth', 'user'],
  },
  // Orders
  orders: {
    all: ['orders'],
    list: (params) => ['orders', 'list', params],
    detail: (id) => ['orders', 'detail', id],
    purchases: (params) => ['orders', 'purchases', params],
  },
  // SKU lookups
  sku: {
    all: ['sku'],
    lookup: (term) => ['sku', 'lookup', term],
    lookupMany: (skuData) => ['sku', 'lookupMany', skuData],
  },
  // Tags
  tags: {
    all: ['tags'],
    active: ['tags', 'active'],
    map: (clientIds, skus) => ['tags', 'map', { clientIds, skus }],
  },
  // App metadata
  app: {
    metadata: ['app', 'metadata'],
    settings: ['app', 'settings'],
  },
};

// Cache management utilities - disabled caching
export const cacheUtils = {
  // Invalidate specific query patterns
  invalidateQueries: (pattern) => {
    return queryClient.invalidateQueries({ queryKey: pattern });
  },
  
  // Remove queries from cache
  removeQueries: (pattern) => {
    return queryClient.removeQueries({ queryKey: pattern });
  },
  
  // Clear all cache immediately
  clearCache: () => {
    return queryClient.clear();
  },
  
  // Prefetch disabled - always fetch fresh data
  prefetchQuery: (key, queryFn, options = {}) => {
    return queryClient.fetchQuery({
      queryKey: key,
      queryFn,
      staleTime: 0,
      gcTime: 0,
      ...options,
    });
  },
};

// Optimistic updates disabled - always fetch fresh data

export default queryClient;
